import { defineStore } from 'pinia';
import { ref, computed } from 'vue';
import type { Item } from '../api';

export interface Tag {
  id: string;
  name: string;
  color: string;
  icon?: string;
  count?: number;
}

const DEFAULT_COLORS = [
  '#ef4444', // red
  '#f97316', // orange
  '#f59e0b', // amber
  '#84cc16', // lime
  '#10b981', // emerald
  '#06b6d4', // cyan
  '#3b82f6', // blue
  '#6366f1', // indigo
  '#8b5cf6', // violet
  '#d946ef', // fuchsia
  '#ec4899', // pink
  '#f43f5e', // rose
];

const DEFAULT_ICONS = [
  '📖', '✅', '💡', '🔖', '📌', '🎯', '⭐', '❤️',
  '🔥', '💪', '🚀', '💻', '📱', '🎨', '📝', '🔬',
  '💼', '📊', '🎵', '📸', '🎬', '🏃', '🍎', '✈️',
];

export const useTagsStore = defineStore('tags', () => {
  const tags = ref<Tag[]>([]);
  const loading = ref(false);

  // 从 localStorage 加载标签
  function loadTags() {
    try {
      const saved = localStorage.getItem('snapclip-tags');
      if (saved) {
        tags.value = JSON.parse(saved);
      }
    } catch (e) {
      console.error('Failed to load tags:', e);
    }
  }

  // 保存标签到 localStorage
  function saveTags() {
    try {
      localStorage.setItem('snapclip-tags', JSON.stringify(tags.value));
    } catch (e) {
      console.error('Failed to save tags:', e);
    }
  }

  // 从 items 中提取标签并统计
  function extractTagsFromItems(items: Item[] = []) {
    const tagMap = new Map<string, { count: number; color?: string; icon?: string }>();

    // 统计每个标签的使用次数
    items.forEach(item => {
      if (item.tags && Array.isArray(item.tags)) {
        item.tags.forEach(tagName => {
          const existing = tagMap.get(tagName);
          if (existing) {
            existing.count++;
          } else {
            tagMap.set(tagName, { count: 1 });
          }
        });
      }
    });

    // 合并 localStorage 中的标签颜色和图标信息
    tags.value.forEach(tag => {
      const existing = tagMap.get(tag.name);
      if (existing) {
        existing.color = tag.color;
        existing.icon = tag.icon;
      }
    });

    // 转换为 Tag 数组
    const extractedTags = Array.from(tagMap.entries()).map(([name, info]) => {
      const existingTag = tags.value.find(t => t.name === name);
      return {
        id: existingTag?.id || `tag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name,
        color: info.color || existingTag?.color || DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)],
        icon: info.icon || existingTag?.icon,
        count: info.count,
      };
    });

    // 更新 tags 数组
    tags.value = extractedTags;
    saveTags();
  }

  // 获取所有标签
  const allTags = computed(() => tags.value);

  // 根据名称查找标签
  function getTagByName(name: string): Tag | undefined {
    return tags.value.find(tag => tag.name === name);
  }

  // 创建标签
  function createTag(name: string, color?: string, icon?: string): Tag {
    const existingTag = getTagByName(name);
    if (existingTag) {
      return existingTag;
    }

    const tag: Tag = {
      id: `tag-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      name,
      color: color || DEFAULT_COLORS[Math.floor(Math.random() * DEFAULT_COLORS.length)],
      icon: icon || DEFAULT_ICONS[Math.floor(Math.random() * DEFAULT_ICONS.length)],
    };

    tags.value.push(tag);
    saveTags();
    return tag;
  }

  // 删除标签
  function deleteTag(tagId: string) {
    const index = tags.value.findIndex(tag => tag.id === tagId);
    if (index !== -1) {
      tags.value.splice(index, 1);
      saveTags();
    }
  }

  // 更新标签
  function updateTag(tagId: string, updates: Partial<Omit<Tag, 'id'>>) {
    const tag = tags.value.find(t => t.id === tagId);
    if (tag) {
      Object.assign(tag, updates);
      saveTags();
    }
  }

  // 获取或创建标签
  function getOrCreateTag(name: string): Tag {
    return createTag(name);
  }

  // 获取标签颜色
  function getTagColor(tagName: string): string {
    const tag = getTagByName(tagName);
    return tag?.color || DEFAULT_COLORS[0];
  }

  // 初始化
  loadTags();

  return {
    tags,
    loading,
    allTags,
    DEFAULT_COLORS,
    DEFAULT_ICONS,
    createTag,
    deleteTag,
    updateTag,
    getOrCreateTag,
    getTagColor,
    getTagByName,
    loadTags,
    saveTags,
    extractTagsFromItems,
  };
});
